type CampaignIntegrityAction =
  | 'create'
  | 'rebuild'
  | 'regenerate'
  | 'revise'
  | 'confirm'
  | 'cycle-lock';

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error && typeof error.message === 'string') {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return '';
}

function sanitizeMessage(message: string): string {
  return message
    .replace(/^createCampaign failed:\s*\d+\s*/i, '')
    .replace(/^\/api\/[^\s]+ failed:\s*\d+\s*-\s*/i, '')
    .replace(/^error:\s*/i, '')
    .trim();
}

function isSnapshotIntegrityFailure(message: string): boolean {
  const normalized = message.toLowerCase();
  return [
    'active non-draft snapshot',
    'missing exactly one',
    'missing a valid snapshot',
    'missing or invalid snapshot',
    'campaign snapshot is missing',
    'campaign snapshot could not be created',
    'snapshot integrity',
    'multiple active',
    'zero active',
    'cycle lock aborted',
  ].some((token) => normalized.includes(token));
}

function getSnapshotIntegrityMessage(action: CampaignIntegrityAction): string {
  switch (action) {
    case 'create':
      return 'This campaign could not be created because its saved campaign snapshot could not be created. No campaign was saved. Please contact support/admin if this keeps happening.';
    case 'cycle-lock':
      return "This cycle can't be locked because one or more confirmed campaigns are missing a valid saved snapshot. This is a historical data issue. Please contact support/admin.";
    default:
      return "This campaign can't be updated because its saved campaign snapshot is missing or invalid. This is a historical data issue. Please contact support/admin.";
  }
}

function getActionFallback(action: CampaignIntegrityAction): string {
  switch (action) {
    case 'create':
      return 'Failed to create campaign.';
    case 'rebuild':
      return 'Failed to save rebuilt campaign.';
    case 'regenerate':
      return 'Failed to regenerate campaign snapshot.';
    case 'revise':
      return 'Failed to create draft revision.';
    case 'confirm':
      return 'Failed to confirm draft revision.';
    case 'cycle-lock':
      return 'Failed to lock cycle.';
  }
}

export function getCampaignIntegrityUserMessage(
  error: unknown,
  action: CampaignIntegrityAction
): string {
  const rawMessage = sanitizeMessage(extractErrorMessage(error));

  if (rawMessage.toLowerCase().includes('no draft revision found')) {
    return 'This campaign draft could not be confirmed because no saved draft revision was found. Refresh the page and try again. If the problem continues, contact support/admin.';
  }

  if (isSnapshotIntegrityFailure(rawMessage)) {
    return getSnapshotIntegrityMessage(action);
  }

  return rawMessage || getActionFallback(action);
}
